import {
  ApplicationConfig,
  isDevMode,
  provideZoneChangeDetection,
} from '@angular/core';
import { environment } from '../environments/environment';
import { provideSentry } from '@techniek-team/sentry-web';
import { provideState, provideStore } from '@ngrx/store';
import {
  provideRouterStore,
  routerReducer,
  RouterState,
} from '@ngrx/router-store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideApplicationStore } from '@lyceo/shared/data-access';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { Drivers, provideStorage } from '@techniek-team/common';
import { ENVIRONMENT } from '@lyceo/homi/shared/util-environment';
import {
  PreloadAllModules,
  provideRouter,
  withPreloading,
} from '@angular/router';
import { appRoutes } from './app.routes';
import { provideEffects } from '@ngrx/effects';
import {
  initialLoginSettingsState,
  LOGIN_SETTINGS_FEATURE_KEY,
  LoginSettingsEffects,
  loginSettingsLocalStorageSyncReducer,
  loginSettingsReducer,
} from '@lyceo/homi/login/data-access';
import { provideTransloco } from '@jsverse/transloco';
import { TranslocoHttpLoaderService } from '@lyceo/shared/translate/feature-main';
import { provideTtFeatureFlags } from '@techniek-team/feature-flags';
import { provideAuth } from '@lyceo/shared/auth/feature-main';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { FEEDBACK_MODAL, provideFeedback } from '@lyceo/shared/feedback/util';
import {
  initialUserState,
  USER_FEATURE_KEY,
  UserEffects,
  userReducer,
} from '@lyceo/homi/user/data-access';
import { withCapacitor } from '@techniek-team/sentry-capacitor';
import { provideHomiSettings } from '@lyceo/shared/homi-settings/data-access';
import { PlanMode, providePlan } from '@lyceo/shared/plan/util-provider';
import { FeedbackModalComponent } from '@lyceo/shared/feedback/feature-main';
import {
  initialSettingsState,
  SETTINGS_FEATURE_KEY,
  SettingsEffects,
  settingsLocalStorageSyncReducer,
  settingsReducer,
} from '@lyceo/homi/settings/data-access';
import { Language } from '@lyceo/homi/settings/util-models';
import { HOMI_STORAGE } from '@lyceo/homi/shared/util';
import { DEFAULT_DIALOG_CONFIG, DialogConfig } from '@angular/cdk/dialog';
import { DialogContainerComponent } from '@lyceo/homi/shared/ui';
import {
  initialInstituteState,
  initialLessonState,
  INSTITUTE_FEATURE_KEY,
  InstituteEffects,
  instituteReducer,
  LESSON_FEATURE_KEY,
  LessonEffects,
  lessonReducer,
  COURSE_FEATURE_KEY,
  CourseEffects,
  courseReducer,
  initialCourseState,
} from '@lyceo/homi/shared/data-access';
import { provideFlashcard } from '@lyceo/shared/flashcards/util';
import { ALERT_MODAL } from '@lyceo/shared/util';
import { AlertModalComponent } from '@lyceo/shared/ui';
import { provideTaskCount } from '@lyceo/shared/task-count/feature-main';
import { providePupilContext } from '@lyceo/shared/pupil-context/data-access';

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ENVIRONMENT, useValue: environment },
    { provide: ALERT_MODAL, useValue: AlertModalComponent },
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(appRoutes, withPreloading(PreloadAllModules)),
    provideStore({
      router: routerReducer,
    }),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      autoPause: true,
      features: {
        pause: false,
        lock: true,
      },
      connectInZone: true,
    }),
    provideRouterStore({
      routerState: RouterState.Minimal,
    }),
    provideApplicationStore({ mobileScheme: 'homiapp' }),
    provideState(LOGIN_SETTINGS_FEATURE_KEY, loginSettingsReducer, {
      initialState: initialLoginSettingsState,
      metaReducers: [loginSettingsLocalStorageSyncReducer],
    }),
    provideState(USER_FEATURE_KEY, userReducer, {
      initialState: initialUserState,
    }),
    provideState(SETTINGS_FEATURE_KEY, settingsReducer, {
      initialState: initialSettingsState,
      metaReducers: [settingsLocalStorageSyncReducer],
    }),
    provideState(LESSON_FEATURE_KEY, lessonReducer, {
      initialState: initialLessonState,
    }),
    provideState(INSTITUTE_FEATURE_KEY, instituteReducer, {
      initialState: initialInstituteState,
    }),
    provideState(COURSE_FEATURE_KEY, courseReducer, {
      initialState: initialCourseState,
    }),
    provideEffects(
      LoginSettingsEffects,
      UserEffects,
      SettingsEffects,
      LessonEffects,
      InstituteEffects,
      CourseEffects,
    ),
    provideSentry(
      {
        environment: environment.environment,
        dsn: environment.sentryDsn,
        release: environment.release,
        autoSessionTracking: true,
        enableTracing: true,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        profilesSampleRate: 0,
      },
      withCapacitor({}),
      //withTracing(),
    ),
    provideStorage({
      name: HOMI_STORAGE,
      driverOrder: [Drivers.LocalStorage],
    }),
    provideTransloco({
      config: {
        availableLangs: Object.values(Language),
        defaultLang: Language.NL,
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoaderService,
    }),
    provideTtFeatureFlags({
      appName: 'homi',
      url: environment.gitlabFeatureFlags.url,
      clientKey: environment.gitlabFeatureFlags.clientKey,
      environment: environment.gitlabFeatureFlags.environment,
      disableRefresh: true,
    }),
    provideAuth({
      authModuleConfig: environment.authModuleConfig,
      storagePrefix: HOMI_STORAGE,
      initialize: false,
      setSentryUser: true,
    }),
    provideIonicAngular({
      rippleEffect: false,
      useSetInputAPI: true,
      animated: false,
    }),
    provideFeedback({
      release: environment.release,
      name: 'Homi',
      email: 'support@homi.nu',
    }),
    { provide: FEEDBACK_MODAL, useValue: FeedbackModalComponent },
    provideHomiSettings({
      homiApiUrl: environment.homiApiUrl,
    }),
    providePlan({
      homiApiUrl: environment.homiApiUrl,
      mode: PlanMode.PUPIL,
      feedback: false,
    }),
    provideFlashcard({
      storagePrefix: HOMI_STORAGE,
      feedback: false,
    }),
    provideZoneChangeDetection(),
    {
      provide: DEFAULT_DIALOG_CONFIG,
      useValue: {
        ...new DialogConfig(),
        container: DialogContainerComponent,
        panelClass: 'dialog-md',
      },
    },
    provideTaskCount({
      homiApiUrl: environment.homiApiUrl,
    }),
    providePupilContext(),
  ],
};
